import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Helmet from 'react-helmet';
import queryString from 'query-string';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import { gridSize } from '@atlaskit/theme';

import { MessageDescriptor, URLParameters } from '../types';
import messages from './ChangePasswordPage.messages';
import TallButton from '../components/TallButton';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import WhiteboxPage from '../components/WhiteboxPage';
import PasswordField from '../components/PasswordField';
import {
  passwordStrengthMessageId,
  PasswordStrengthMeter,
} from '../components/PasswordStrengthMeter/PasswordStrengthMeter';
import FieldGroup from '../components/FieldGroup';
import { ChangePasswordType, requestChangePassword } from '../actions/change-password-actions';
import {
  PASSWORD_LEAKED_LINK,
  PrivacyPolicyLink,
  SELF_SERVICE_LINK_URL,
  SupportLink,
  UserNoticeLink,
} from '../components/links';
import { getMicrobrandingFromUrl } from '../utilities/applications';
import trustAndSecurity from '../images/trust-and-security.svg';
import Bull from '../components/BulletSeparator';
import { SectionError } from '../components/FormError';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useTranslateFormErrorIfAvailable } from '../pages/signup/FormValidation.messages';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import Heading from '@atlaskit/heading';

const TRANSITION_DURATION = 400;

const SecurityContainer = styled.div`
  background: url(${trustAndSecurity}) no-repeat center;
  height: ${gridSize() * 14.5}px;
  transition: visibility ${TRANSITION_DURATION}ms;
  margin-bottom: ${gridSize() * 1.5}px;
`;

const Form = styled.form`
  margin-top: 0;
`;

export type ChangeEmailFlowMessage = 'change_email';
export type SecurityWasMeForcedResetPasswordMessage = 'security_wasme_reset';
export type SecurityWasNotMeForcedResetPasswordMessage = 'security_wasnotme_reset';

const knownMessageKeys: URLParameters['message'][] = [
  'change_email',
  'security_wasme_reset',
  'security_wasnotme_reset',
];

function chooseTitle(message: URLParameters['message']): MessageDescriptor {
  switch (message) {
    case 'security_wasme_reset':
      return messages.securityResetTitle;
    case 'security_wasnotme_reset':
      return messages.securityResetTitle;
    default:
      return messages.title;
  }
}

function chooseButtonText(message: URLParameters['message']): MessageDescriptor {
  switch (message) {
    case 'security_wasme_reset':
      return messages.button;
    case 'security_wasnotme_reset':
      return messages.button;
    default:
      return messages.button;
  }
}

function chooseAnalyticsEventName(message: URLParameters['message']): string {
  switch (message) {
    case 'security_wasme_reset':
      return 'suspiciousWasUser';
    case 'security_wasnotme_reset':
      return 'suspiciousWasNotUser';
    case 'change_email':
      return 'changeEmail';
    default:
      return 'userRequest';
  }
}

function chooseChangePasswordType(message: URLParameters['message']): ChangePasswordType {
  switch (message) {
    case 'security_wasme_reset':
      return 'security-password-reset';
    case 'security_wasnotme_reset':
      return 'security-password-reset';
    default:
      return 'password-reset';
  }
}

const pageId = 'setNewPasswordScreen';

export async function changePasswordLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/changepassword/state', request, {
    capabilities: ['capability-group--manage-profile', 'capability-name--change-password'],
  });
}

export const ChangePasswordPage = () => {
  const dispatch = useAppDispatch();

  const email = useAppSelector(state => state.changePassword.email);
  const displayName = useAppSelector(state => state.changePassword.displayName);
  const isLoading = useAppSelector(state => state.changePassword.isLoading);
  const error = useAppSelector(state => state.changePassword.error);
  const csrfToken = useAppSelector(state => state.csrfToken);
  const intl = useIntl();
  const passwordFieldError = useAppSelector(state => state.changePassword.fieldErrors.password);
  const translatedPasswordFieldError = useTranslateFormErrorIfAvailable(passwordFieldError);
  const location = useLocation();
  const analyticsClient = useAnalyticsClient();

  const [password, setPassword] = useState('');
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  const query = useMemo(() => {
    return queryString.parse(location.search) as URLParameters;
  }, [location.search]);

  usePageViewedEvent(pageId, { origin: chooseAnalyticsEventName(query.message) });

  useEffect(() => {
    if (query.message && !knownMessageKeys.includes(query.message)) {
      captureErrorMessage('change_password_missing_error_message', { key: query.message });
    }
  }, [query.message]);

  const renderPasswordChangeError = error => {
    switch (error) {
      case 'unknown':
        return (
          <FormattedMessage
            id="changePassword.error.unknown"
            defaultMessage="Something went wrong while attempting to change your password. Please try again."
          />
        );
      case 'request_error':
        return (
          <FormattedMessage
            id="changePassword.error.request_error"
            defaultMessage="Unable to connect. Check your internet connection or the network settings for your browser and try again."
          />
        );
      case 'passwordLeaked':
        return (
          <FormattedMessage
            id="changepassword.password.leaked.paragraph.xml"
            defaultMessage="<p>This password has appeared in an external data breach. To continue, enter another password. <learn-more-link>Learn more</learn-more-link></p>"
            tagName="p"
            values={{
              p: chunks => <p>{chunks}</p>,
              'learn-more-link': chunks => <a href={PASSWORD_LEAKED_LINK}>{chunks}</a>,
            }}
          />
        );
      case 'passwordCommon':
        return (
          <FormattedMessage
            id="changePassword.error.passwordCommon"
            defaultMessage="This password is too widely used. Choose a password that's harder to guess."
          />
        );
      case 'invalidPassword':
        return (
          <FormattedMessage
            id="changePassword.error.invalidPassword"
            defaultMessage="Invalid password"
          />
        );
      case 'passwordCaseError':
        return (
          <FormattedMessage
            id="changePassword.error.passwordCaseError"
            defaultMessage="Your password must contain at least one uppercase and one lowercase letter."
          />
        );
      case 'passwordDigitOrSpecialError':
        return (
          <FormattedMessage
            id="changePassword.error.passwordDigitOrSpecialError"
            defaultMessage="Your password must contain at least one digit or special character."
          />
        );
      case 'passwordLengthError':
        return (
          <FormattedMessage
            id="changePassword.error.passwordLengthError"
            defaultMessage="Your password must be between 8 and 72 characters long."
          />
        );
      case 'passwordUsedBefore':
        return (
          <FormattedMessage
            id="changePassword.error.passwordUsedBefore"
            defaultMessage="You've used that password before. Please choose a different one."
          />
        );
      case 'passwordStrengthErrorWeak':
        return (
          <FormattedMessage
            id="changePassword.error.passwordStrengthErrorWeak"
            defaultMessage="Your password must have a minimum strength of weak."
          />
        );
      case 'passwordStrengthErrorFair':
        return (
          <FormattedMessage
            id="changePassword.error.passwordStrengthErrorFair"
            defaultMessage="Your password must have a minimum strength of fair."
          />
        );
      case 'passwordStrengthErrorGood':
        return (
          <FormattedMessage
            id="changePassword.error.passwordStrengthErrorGood"
            defaultMessage="Your password must have a minimum strength of good."
          />
        );
      case 'passwordStrengthErrorStrong':
        return (
          <FormattedMessage
            id="changePassword.error.passwordStrengthErrorStrong"
            defaultMessage="Your password must have a minimum strength of strong."
          />
        );
      case 'passwordStrengthErrorVeryStrong':
        return (
          <FormattedMessage
            id="changePassword.error.passwordStrengthErrorVeryStrong"
            defaultMessage="Your password must have a minimum strength of very strong."
          />
        );
      default:
        return (
          <FormattedMessage
            id="changePassword.form.random.error"
            defaultMessage="An error occurred while attempting to sign up: {error}. Please try again or reach out to Atlassian Support."
            values={{ error }}
          />
        );
    }
  };

  const renderWhiteboxFooter = () => {
    return (
      <a
        id="changepassword-selfservice-link"
        href={SELF_SERVICE_LINK_URL}
        onClick={() => analyticsClient.linkClickedEvent(pageId, 'stillHavingTroubleLogin')}>
        <FormattedMessage
          id="changepassword.self.service"
          defaultMessage="Still having trouble logging in?"
        />
      </a>
    );
  };

  const renderFooter = () => {
    return (
      <ul>
        <li>
          <SupportLink />
        </li>
        <Bull />
        <li>
          <UserNoticeLink isMobileLink={false} />
        </li>
        <Bull />
        <li>
          <PrivacyPolicyLink isMobileLink={false} />
        </li>
      </ul>
    );
  };

  const handlePasswordChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setPassword(e.currentTarget.value);
    },
    [setPassword]
  );

  const navigate = useNavigateSoftOrHard();
  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const { signature, message } = query;

      analyticsClient.buttonClickedEvent(pageId, 'continueToSetNewPassword', {
        origin: chooseAnalyticsEventName(message),
      });

      dispatch(
        requestChangePassword(
          {
            signature: signature || '',
            csrfToken,
            password,
            resetType: chooseChangePasswordType(message),
            microbranding: getMicrobrandingFromUrl(location),
            intl,
            analyticsClient,
          },
          navigate
        )
      );
    },
    [query, analyticsClient, dispatch, csrfToken, password, location, intl, navigate]
  );

  const { formatMessage } = intl;

  const { message } = query;
  const title = formatMessage(chooseTitle(message));

  return (
    <WhiteboxPage
      pageId={pageId}
      header={
        <Heading size="small" as="h1">
          {title}
        </Heading>
      }
      footer={renderFooter()}
      whiteboxFooter={renderWhiteboxFooter()}>
      {(message === 'security_wasme_reset' || message === 'security_wasnotme_reset') && (
        <SecurityContainer />
      )}

      <Helmet title={formatMessage(chooseTitle(message))} />

      {error && <SectionError>{renderPasswordChangeError(error)}</SectionError>}

      {message === 'security_wasme_reset' && (
        <div id="security-wasme-reset-message">
          <FormattedMessage
            id="changepassword.security.wasme.reset.paragraph.xml"
            defaultMessage="<p>Thanks for helping us keep your account secure.</p><p>To prevent unauthorized access to your account, your password has been invalidated.</p><p>Create a new, unique password to access your account:</p><p-email>{email}</p-email>"
            values={{
              email,
              p: chunks => <p>{chunks}</p>,
              'p-email': chunks => (
                <p style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <strong>{chunks}</strong>
                </p>
              ),
            }}
          />
        </div>
      )}
      {message === 'security_wasnotme_reset' && (
        <div id="security-wasnotme-reset-message">
          <FormattedMessage
            id="changepassword.security.wasnotme.reset.paragraph.xml"
            defaultMessage="<p>To prevent unauthorized access to your account, your password has been invalidated.</p><p>Create a new, unique password to access your account: </p><p-email>{email}</p-email>"
            values={{
              email,
              p: chunks => <p>{chunks}</p>,
              'p-email': chunks => (
                <p style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <strong>{chunks}</strong>
                </p>
              ),
            }}
          />
        </div>
      )}

      <Form autoComplete="on" onSubmit={handleSubmit}>
        <FieldGroup>
          <PasswordField
            id="password"
            testId="password" // used in pollinators
            name="password"
            autoComplete="new-password"
            aria-describedby={passwordStrengthMessageId}
            placeholder={formatMessage(messages.passwordPlaceholder)}
            label={formatMessage(messages.passwordLabel)}
            isInvalid={!!translatedPasswordFieldError}
            invalidMessage={translatedPasswordFieldError}
            onChange={handlePasswordChange}
            value={password}
            ref={passwordInputRef}
            smallPlaceholder
          />
          <PasswordStrengthMeter password={password} email={email} displayName={displayName} />
        </FieldGroup>
        <TallButton
          appearance="primary"
          id="change-password"
          type="submit"
          isFullWidth
          isLoading={isLoading}>
          {formatMessage(chooseButtonText(message))}
        </TallButton>
      </Form>
    </WhiteboxPage>
  );
};

export default ChangePasswordPage;
